import React from "react";
import { DataCell } from "@/components/DataCell";
import { PercentChange } from "@/components/DataCell/PercentChange";
import { useTranslation } from "react-i18next";
import { useContextMetrics } from "@/hooks/useContextMetrics";
import { OrganizationMetricsContextEnum } from "@/__generated__/types";

interface OverviewDataCellsProps {
  orgId?: string;
  orgIdLoading?: boolean;
  orgIdError?: unknown;
}

export const OverviewDataCells: React.FC<OverviewDataCellsProps> = ({
  orgId,
  orgIdLoading = false,
  orgIdError,
}) => {
  const { t } = useTranslation();

  const {
    loading: metricsLoading,
    error: metricsError,
    getPreviousCycleValue,
    getMetricDisplayValue,
    getMetricCalculatedPercentChanged,
  } = useContextMetrics(orgId, OrganizationMetricsContextEnum.CommunityTools);

  const isLoading = orgIdLoading;
  const hasError = orgIdError;
  const hasRequiredData = Boolean(orgId);

  const showLoading = () => {
    if (hasError || metricsError) return false;
    return isLoading || metricsLoading;
  };

  const showError = () => {
    return hasError || metricsError || (!hasRequiredData && !isLoading);
  };

  const errorContent = (
    <span className="text-sm text-muted-foreground">
      {t("bulkMessaging.communityToolsOverview.bentos.error.loadingError")}
    </span>
  );

  return (
    <div className="flex flex-col gap-5">
      <DataCell
        className="min-h-[136px]"
        isLoading={showLoading()}
        title={t(
          "bulkMessaging.communityToolsOverview.bentos.studentInteractions",
        )}
        tooltipText={t(
          "bulkMessaging.communityToolsOverview.bentos.studentInteractionsToolTip",
        )}
        middleValue={
          showError()
            ? errorContent
            : getMetricDisplayValue("studentsInteractions")
        }
        bottomValue={
          showError() ? null : (
            <PercentChange
              value={getMetricCalculatedPercentChanged("studentsInteractions")}
              prevCount={getPreviousCycleValue("studentsInteractions")}
              customTemplate={t("metrics.comparisons.yearOverYear", {
                percentChanged: "{{percentChange}}",
              })}
            />
          )
        }
      />

      <DataCell
        className="min-h-[136px]"
        isLoading={showLoading()}
        title={t(
          "bulkMessaging.communityToolsOverview.bentos.totalCampaignsSent",
        )}
        tooltipText={t(
          "bulkMessaging.communityToolsOverview.bentos.totalCampaignsSentToolTip",
        )}
        middleValue={
          showError()
            ? errorContent
            : getMetricDisplayValue("totalCampaignsSent")
        }
        bottomValue={
          showError() ? null : (
            <PercentChange
              value={getMetricCalculatedPercentChanged("totalCampaignsSent")}
              prevCount={getPreviousCycleValue("totalCampaignsSent")}
              customTemplate={t("metrics.comparisons.yearOverYear", {
                percentChanged: "{{percentChange}}",
              })}
            />
          )
        }
      />
    </div>
  );
};
